import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from './Constants';

const FtpFileBrowser = ({ isOpen, onClose, onSelectFile, fileFilter, title = 'Quản lý file trên Server' }) => {
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({ '/': true });
  const [subFolders, setSubFolders] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);

  // Load files in a directory
  const loadFiles = useCallback(async (directory) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/upload/ftp/list?directory=${encodeURIComponent(directory)}`
      );
      const data = await response.json();
      setFiles(data);
    } catch (error) {
      console.error('Error loading FTP files:', error);
      setFiles([]);
    }
    setIsLoading(false);
  }, []);

  // Load subdirectories for folder tree
  const loadSubDirectories = useCallback(async (directory) => {
    try {
      const response = await fetch(
        `${API_URL}/upload/ftp/directories?directory=${encodeURIComponent(directory)}`
      );
      const data = await response.json();
      setSubFolders((prev) => ({ ...prev, [directory]: data }));
      return data;
    } catch (error) {
      console.error('Error loading FTP directories:', error);
      return [];
    }
  }, []);

  // Load root directories on open
  useEffect(() => {
    if (isOpen) {
      loadFiles('/');
      loadSubDirectories('/');
      setCurrentPath('/');
      setSelectedFile(null);
      setExpandedFolders({ '/': true });
    }
  }, [isOpen, loadFiles, loadSubDirectories]);

  // Handle folder click in tree
  const handleFolderClick = async (folderPath) => {
    setCurrentPath(folderPath);
    setSelectedFile(null);
    loadFiles(folderPath);

    // Toggle expand
    const isExpanded = expandedFolders[folderPath];
    if (!isExpanded) {
      setExpandedFolders((prev) => ({ ...prev, [folderPath]: true }));
      if (!subFolders[folderPath]) {
        await loadSubDirectories(folderPath);
      }
    } else {
      setExpandedFolders((prev) => ({ ...prev, [folderPath]: false }));
    }
  };

  // Handle file click in right panel
  const handleFileClick = (file) => {
    if (file.isDirectory) {
      handleFolderClick(file.path);
    } else {
      setSelectedFile(file);
    }
  };

  // Format file path for selection (remove /LinhQuyPhapAn prefix if exists)
  const getProcessedFile = (file) => {
    let newPath = file.path;
    if (newPath.startsWith('/LinhQuyPhapAn')) {
      newPath = newPath.substring('/LinhQuyPhapAn'.length);
    }
    return { ...file, path: newPath };
  };

  // Handle file double click
  const handleFileDoubleClick = (file) => {
    if (file.isDirectory) {
      handleFolderClick(file.path);
    } else {
      onSelectFile(getProcessedFile(file));
      onClose();
    }
  };

  // Handle select button
  const handleSelect = () => {
    if (selectedFile) {
      onSelectFile(getProcessedFile(selectedFile));
      onClose();
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('directory', currentPath);

    try {
      const response = await fetch(`${API_URL}/upload/ftp/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        loadFiles(currentPath);
      } else {
        alert('Tải file lên thất bại.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Có lỗi xảy ra khi tải file.');
    } finally {
      setIsUploading(false);
      event.target.value = null;
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get file icon
  const getFileIcon = (file) => {
    if (file.isDirectory) return '📁';
    const ext = file.name.split('.').pop().toLowerCase();
    switch (ext) {
      case 'mp3': case 'wav': case 'ogg': case 'flac': return '🎵';
      case 'mp4': case 'avi': case 'mkv': case 'mov': return '🎬';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return '🖼️';
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'zip': case 'rar': case '7z': return '📦';
      default: return '📄';
    }
  };

  // Render folder tree recursively
  const renderFolderTree = (parentPath, depth = 0) => {
    const folders = subFolders[parentPath] || [];
    return folders.map((folder) => {
      const isExpanded = expandedFolders[folder.path];
      const isActive = currentPath === folder.path;
      return (
        <div key={folder.path}>
          <div
            onClick={() => handleFolderClick(folder.path)}
            style={{
              ...styles.treeItem,
              paddingLeft: `${12 + depth * 16}px`,
              backgroundColor: isActive ? '#e8f0fe' : 'transparent',
              borderLeft: isActive ? '3px solid #1a73e8' : '3px solid transparent',
              fontWeight: isActive ? '600' : '400',
            }}
          >
            <span style={styles.treeToggle}>
              {isExpanded ? '▾' : '▸'}
            </span>
            <span style={{ marginRight: '6px', fontSize: '14px' }}>
              {isExpanded ? '📂' : '📁'}
            </span>
            <span style={styles.treeItemName}>{folder.name}</span>
          </div>
          {isExpanded && renderFolderTree(folder.path, depth + 1)}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  // Filter files to display based on fileFilter
  const displayFiles = (!showAllFiles && fileFilter)
    ? files.filter((f) => {
        if (f.isDirectory) return true;
        const lowerName = f.name.toLowerCase();
        if (Array.isArray(fileFilter)) {
          return fileFilter.some(ext => lowerName.endsWith(ext.toLowerCase()));
        }
        return lowerName.endsWith(typeof fileFilter === 'string' ? fileFilter.toLowerCase() : fileFilter);
      })
    : files;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>📂</span>
            <h3 style={styles.headerTitle}>{title}</h3>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {/* Toolbar */}
        <div style={{...styles.toolbar, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={styles.breadcrumb}>
            <span
              onClick={() => handleFolderClick('/')}
              style={styles.breadcrumbItem}
            >
              🏠 Root
            </span>
            {currentPath !== '/' &&
              currentPath
                .split('/')
                .filter(Boolean)
                .map((part, index, arr) => {
                  const path = '/' + arr.slice(0, index + 1).join('/');
                  return (
                    <span key={path}>
                      <span style={styles.breadcrumbSep}>/</span>
                      <span
                        onClick={() => handleFolderClick(path)}
                        style={styles.breadcrumbItem}
                      >
                        {part}
                      </span>
                    </span>
                  );
                })}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
            {fileFilter && (
              <label style={{ cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', color: '#555' }}>
                <input 
                  type="checkbox" 
                  checked={showAllFiles} 
                  onChange={(e) => setShowAllFiles(e.target.checked)} 
                />
                Hiển thị tất cả định dạng
              </label>
            )}
            <input 
              type="file" 
              id="ftp-upload-input" 
              style={{display: 'none'}} 
              onChange={handleFileUpload} 
            />
            <button 
              style={{
                ...styles.uploadBtn,
                ...(isUploading ? styles.uploadBtnDisabled : {})
              }}
              onClick={() => document.getElementById('ftp-upload-input').click()}
              disabled={isUploading}
            >
              {isUploading ? 'Đang tải...' : '⬆ Tải lên'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Left panel - Folder tree */}
          <div style={styles.leftPanel}>
            <div style={styles.panelHeader}>
              <span style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>Folders</span>
            </div>
            <div style={styles.treeContainer}>
              <div
                onClick={() => handleFolderClick('/')}
                style={{
                  ...styles.treeItem,
                  backgroundColor: currentPath === '/' ? '#e8f0fe' : 'transparent',
                  borderLeft: currentPath === '/' ? '3px solid #1a73e8' : '3px solid transparent',
                  fontWeight: currentPath === '/' ? '600' : '400',
                }}
              >
                <span style={styles.treeToggle}>
                  {expandedFolders['/'] ? '▾' : '▸'}
                </span>
                <span style={{ marginRight: '6px', fontSize: '14px' }}>📁</span>
                <span style={styles.treeItemName}>Files</span>
              </div>
              {expandedFolders['/'] && renderFolderTree('/')}
            </div>
          </div>

          {/* Right panel - File list */}
          <div style={styles.rightPanel}>
            <div style={styles.panelHeader}>
              <span style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                {currentPath === '/' ? 'Root' : currentPath.split('/').pop()}
              </span>
              <span style={{ fontSize: '12px', color: '#888', marginLeft: '10px' }}>
                {displayFiles.length} items
              </span>
            </div>

            {isLoading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <span style={{ color: '#666', marginTop: '10px' }}>Đang tải...</span>
              </div>
            ) : (
              <div style={styles.fileGrid}>
                {displayFiles.map((file) => (
                  <div
                    key={file.path}
                    onClick={() => handleFileClick(file)}
                    onDoubleClick={() => handleFileDoubleClick(file)}
                    style={{
                      ...styles.fileCard,
                      ...(selectedFile?.path === file.path ? styles.fileCardSelected : {}),
                    }}
                  >
                    <div style={styles.fileIcon}>
                      {getFileIcon(file)}
                    </div>
                    <div style={styles.fileName}>
                      {file.name}
                    </div>
                    <div style={styles.fileDetails}>
                      {file.isDirectory ? 'Folder' : formatSize(file.size)}
                    </div>
                  </div>
                ))}
                {displayFiles.length === 0 && (
                  <div style={styles.emptyState}>
                    <span style={{ fontSize: '40px' }}>📭</span>
                    <p style={{ color: '#888', marginTop: '10px' }}>Thư mục trống</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.selectedInfo}>
            {selectedFile && (
              <span>
                Đã chọn: <strong>{selectedFile.name}</strong> ({formatSize(selectedFile.size)})
              </span>
            )}
          </div>
          <div>
            <button onClick={onClose} style={styles.cancelBtn}>
              Hủy
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedFile}
              style={{
                ...styles.selectBtn,
                ...(selectedFile ? {} : styles.selectBtnDisabled),
              }}
            >
              Chọn file
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ftpSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .ftp-file-card:hover {
          background-color: #f0f6ff !important;
          border-color: #b8d4ff !important;
        }
      `}</style>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(3px)',
  },
  modal: {
    width: '900px',
    maxWidth: '95vw',
    height: '620px',
    maxHeight: '90vh',
    backgroundColor: '#fff',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #1a73e8, #0d47a1)',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    fontSize: '22px',
  },
  headerTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    letterSpacing: '0.3px',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },
  toolbar: {
    padding: '8px 16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '2px',
    fontSize: '13px',
  },
  breadcrumbItem: {
    cursor: 'pointer',
    color: '#1a73e8',
    padding: '3px 6px',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  breadcrumbSep: {
    color: '#999',
    margin: '0 2px',
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  leftPanel: {
    width: '220px',
    minWidth: '220px',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fafafa',
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
  },
  treeContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#333',
    transition: 'background 0.15s',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  treeToggle: {
    width: '16px',
    fontSize: '10px',
    color: '#888',
    marginRight: '4px',
    flexShrink: 0,
  },
  treeItemName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileGrid: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '10px',
    alignContent: 'start',
  },
  fileCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 8px',
    borderRadius: '8px',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  fileCardSelected: {
    backgroundColor: '#e8f0fe',
    borderColor: '#1a73e8',
  },
  fileIcon: {
    fontSize: '36px',
    marginBottom: '6px',
  },
  fileName: {
    fontSize: '12px',
    color: '#333',
    wordBreak: 'break-word',
    lineHeight: '1.3',
    maxHeight: '34px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  fileDetails: {
    fontSize: '11px',
    color: '#999',
    marginTop: '4px',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e0e0e0',
    borderTop: '3px solid #1a73e8',
    borderRadius: '50%',
    animation: 'ftpSpin 0.8s linear infinite',
  },
  emptyState: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  selectedInfo: {
    fontSize: '13px',
    color: '#555',
  },
  cancelBtn: {
    padding: '8px 20px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#555',
    fontSize: '13px',
    cursor: 'pointer',
    marginRight: '10px',
    transition: 'all 0.2s',
  },
  selectBtn: {
    padding: '8px 24px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#1a73e8',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  uploadBtn: {
    padding: '6px 12px',
    border: '1px solid #1a73e8',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#1a73e8',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadBtnDisabled: {
    borderColor: '#ccc',
    color: '#999',
    cursor: 'not-allowed',
  },
};

export default FtpFileBrowser;
